import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  GiftOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { cdkAPI, configAPI } from '../services/api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const MAX_REWARD_COUNT = 2147483647;

function normalizeDate(value) {
  return value ? dayjs(value) : null;
}

function buildUniqueId(itemId, level) {
  return `${itemId}_${level || 0}`;
}

function getStatusTag(status) {
  const normalized = String(status || '');
  if (normalized === 'sent') {
    return <Tag color="success" icon={<CheckCircleOutlined />}>已发放</Tag>;
  }
  if (normalized === 'sending') {
    return <Tag color="processing" icon={<SyncOutlined spin />}>发放中</Tag>;
  }
  if (normalized === 'failed') {
    return <Tag color="error" icon={<CloseCircleOutlined />}>失败</Tag>;
  }
  return <Tag color="warning">待发放</Tag>;
}

function CdkManagement() {
  const [loading, setLoading] = useState(false);
  const [cdks, setCdks] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingCdk, setEditingCdk] = useState(null);
  const [currentCdk, setCurrentCdk] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [form] = Form.useForm();
  const { current: pageCurrent, pageSize } = pagination;

  const loadItems = useCallback(async () => {
    try {
      const response = await configAPI.getItems();
      const items = response.data?.data || [];
      setItemList(items.map((item) => ({
        id: Number(item.item_id || item.id),
        name: item.name || `道具${item.item_id || item.id}`,
        type: item.type || 'item',
        levels: item.levels || [],
        defaultLevel: Number(item.defaultLevel ?? 0)
      })).filter((item) => item.id > 0));
    } catch (error) {
      message.error('加载物品列表失败');
    }
  }, []);

  const loadCdks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await cdkAPI.getList({
        page: pageCurrent,
        limit: pageSize,
        keyword
      });
      const payload = response.data?.data || {};
      setCdks(payload.cdks || []);
      setPagination((prev) => ({
        ...prev,
        total: payload.pagination?.total || 0
      }));
    } catch (error) {
      message.error('加载CDK列表失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, pageCurrent, pageSize]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    loadCdks();
  }, [loadCdks]);

  const itemMap = useMemo(() => new Map(itemList.map((item) => [Number(item.id), item])), [itemList]);

  const filteredItems = useMemo(() => {
    const text = itemSearch.trim().toLowerCase();
    if (!text) {
      return itemList;
    }
    return itemList.filter((item) => (
      String(item.id).includes(text) || String(item.name || '').toLowerCase().includes(text)
    ));
  }, [itemList, itemSearch]);

  const openCreate = () => {
    setEditingCdk(null);
    setSelectedItems([]);
    form.resetFields();
    form.setFieldsValue({
      status: true,
      totalLimit: 0,
      mailContent: '您的CDK奖励已发放，请查收。'
    });
    setEditorOpen(true);
  };

  const openEdit = (record) => {
    setEditingCdk(record);
    const items = (record.items || []).map((item) => {
      const gid = Number(item.gid || item.item_id || item.id);
      const catalog = itemMap.get(gid) || {};
      const level = Number(item.level || 0);
      return {
        id: gid,
        name: item.name || catalog.name || `道具${gid}`,
        levels: catalog.levels || [],
        defaultLevel: catalog.defaultLevel ?? 0,
        level,
        count: Number(item.count || 1),
        uniqueId: buildUniqueId(gid, level)
      };
    });
    setSelectedItems(items);
    form.setFieldsValue({
      code: record.code,
      name: record.name,
      description: record.description || '',
      mailTitle: record.mail_title || record.name,
      mailContent: record.mail_content || '您的CDK奖励已发放，请查收。',
      totalLimit: Number(record.total_limit || 0),
      startTime: normalizeDate(record.start_time),
      endTime: normalizeDate(record.end_time),
      status: Number(record.status) === 1
    });
    setEditorOpen(true);
  };

  const addItem = (item) => {
    const firstLevel = item.levels?.[0]?.level ?? item.defaultLevel ?? 0;
    const uniqueId = buildUniqueId(item.id, firstLevel);
    if (selectedItems.some((selected) => selected.uniqueId === uniqueId)) {
      message.info('该物品已添加');
      return;
    }
    setSelectedItems((prev) => [
      ...prev,
      {
        ...item,
        level: Number(firstLevel || 0),
        count: 1,
        uniqueId
      }
    ]);
  };

  const updateSelectedItem = (uniqueId, patch) => {
    setSelectedItems((prev) => prev.map((item) => {
      if (item.uniqueId !== uniqueId) {
        return item;
      }
      const next = { ...item, ...patch };
      if (patch.level !== undefined) {
        next.uniqueId = buildUniqueId(item.id, patch.level);
      }
      return next;
    }));
  };

  const removeSelectedItem = (uniqueId) => {
    setSelectedItems((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
  };

  const buildItemsPayload = () => selectedItems.map((item) => ({
    gid: item.id,
    name: item.name,
    level: Number(item.level || 0),
    count: Number(item.count || 1)
  }));

  const saveCdk = async (values) => {
    const items = buildItemsPayload();
    if (items.length === 0) {
      message.warning('请至少选择一个奖励物品');
      return;
    }

    const payload = {
      code: values.code,
      name: values.name,
      description: values.description || '',
      mail_title: values.mailTitle || values.name,
      mail_content: values.mailContent || '您的CDK奖励已发放，请查收。',
      items,
      total_limit: values.totalLimit || 0,
      start_time: values.startTime ? values.startTime.format('YYYY-MM-DD HH:mm:ss') : null,
      end_time: values.endTime ? values.endTime.format('YYYY-MM-DD HH:mm:ss') : null,
      status: values.status ? 1 : 0
    };

    try {
      if (editingCdk) {
        await cdkAPI.update(editingCdk.id, payload);
        message.success('CDK更新成功');
      } else {
        await cdkAPI.create(payload);
        message.success('CDK创建成功');
      }
      setEditorOpen(false);
      loadCdks();
    } catch (error) {
      message.error(error.response?.data?.message || '保存CDK失败');
    }
  };

  const deleteCdk = async (id) => {
    try {
      await cdkAPI.delete(id);
      message.success('CDK删除成功');
      loadCdks();
    } catch (error) {
      message.error(error.response?.data?.message || '删除CDK失败');
    }
  };

  const toggleStatus = async (record, checked) => {
    try {
      await cdkAPI.update(record.id, { ...record, status: checked ? 1 : 0, items: record.items || [] });
      message.success(checked ? 'CDK已启用' : 'CDK已停用');
      loadCdks();
    } catch (error) {
      message.error(error.response?.data?.message || '状态更新失败');
    }
  };

  const loadLogs = async (cdkId, page = 1, pageSize = logsPagination.pageSize) => {
    setLogsLoading(true);
    try {
      const response = await cdkAPI.getLogs({
        cdk_id: cdkId,
        page,
        limit: pageSize
      });
      const payload = response.data?.data || {};
      setLogs(payload.logs || []);
      setLogsPagination({
        current: page,
        pageSize,
        total: payload.pagination?.total || 0
      });
    } catch (error) {
      message.error('加载领取记录失败');
    } finally {
      setLogsLoading(false);
    }
  };

  const openLogs = (record) => {
    setCurrentCdk(record);
    setLogsOpen(true);
    loadLogs(record.id, 1, logsPagination.pageSize);
  };

  const retryClaim = async (claimId) => {
    try {
      const response = await cdkAPI.retryClaim(claimId);
      const result = response.data?.data || {};
      message[result.delivered ? 'success' : 'warning'](response.data?.message || '补发已提交');
      if (currentCdk?.id) {
        loadLogs(currentCdk.id, logsPagination.current, logsPagination.pageSize);
      }
    } catch (error) {
      message.error(error.response?.data?.message || '补发失败');
    }
  };

  const renderItems = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return '-';
    }
    return (
      <Space wrap>
        {items.map((item, index) => {
          const gid = item.gid || item.item_id || item.id;
          return (
            <Tag key={`${gid}-${item.level || 0}-${index}`} color="blue">
              {item.name || `道具${gid}`}{item.level ? ` Lv.${item.level}` : ''} x{item.count}
            </Tag>
          );
        })}
      </Space>
    );
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    {
      title: 'CDK',
      dataIndex: 'code',
      width: 180,
      render: (text) => <Text code copyable>{text}</Text>
    },
    {
      title: '名称',
      dataIndex: 'name',
      width: 180,
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 96,
      render: (status, record) => (
        <Switch
          checked={Number(status) === 1}
          onChange={(checked) => toggleStatus(record, checked)}
          checkedChildren="启用"
          unCheckedChildren="停用"
        />
      )
    },
    {
      title: '领取次数',
      width: 120,
      render: (_, record) => `${record.claim_count || 0} / ${record.total_limit > 0 ? record.total_limit : '不限'}`
    },
    {
      title: '有效期',
      width: 210,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.start_time ? new Date(record.start_time).toLocaleString() : '立即开始'}</Text>
          <Text type="secondary">{record.end_time ? new Date(record.end_time).toLocaleString() : '永久有效'}</Text>
        </Space>
      )
    },
    {
      title: '操作',
      width: 210,
      render: (_, record) => (
        <Space>
          <Tooltip title="详情">
            <Button size="small" icon={<EyeOutlined />} onClick={() => { setCurrentCdk(record); setDetailOpen(true); }} />
          </Tooltip>
          <Tooltip title="编辑">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="领取记录">
            <Button size="small" icon={<SearchOutlined />} onClick={() => openLogs(record)} />
          </Tooltip>
          <Popconfirm title="确定删除这个CDK？" onConfirm={() => deleteCdk(record.id)} okText="确定" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} disabled={Number(record.claim_count) > 0} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const logColumns = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: '大区', dataIndex: 'server_id', width: 80, render: (value) => <Tag color="blue">S{value}</Tag> },
    { title: '账号ID', dataIndex: 'account_id', width: 100 },
    { title: '根账号', dataIndex: 'root_account_id', width: 100 },
    { title: '角色ID', dataIndex: 'game_user_id', width: 100 },
    { title: '角色名', dataIndex: 'character_name', width: 130 },
    { title: '状态', dataIndex: 'status', width: 100, render: getStatusTag },
    { title: '尝试', dataIndex: 'attempt_count', width: 70 },
    { title: '错误', dataIndex: 'error_msg', ellipsis: true },
    { title: '领取时间', dataIndex: 'claimed_at', width: 170, render: (value) => value ? new Date(value).toLocaleString() : '-' },
    { title: '发放时间', dataIndex: 'delivered_at', width: 170, render: (value) => value ? new Date(value).toLocaleString() : '-' },
    {
      title: '操作',
      width: 88,
      render: (_, record) => record.status !== 'sent' ? (
        <Button size="small" icon={<SyncOutlined />} onClick={() => retryClaim(record.id)}>补发</Button>
      ) : '-'
    }
  ];

  const selectedColumns = [
    { title: '物品', dataIndex: 'name', render: (_, record) => <Text>{record.name}<br /><Text type="secondary">ID {record.id}</Text></Text> },
    {
      title: '等级',
      width: 130,
      render: (_, record) => {
        const levels = Array.isArray(record.levels) ? record.levels : [];
        if (levels.length === 0) {
          return <InputNumber min={0} value={record.level || 0} onChange={(value) => updateSelectedItem(record.uniqueId, { level: Number(value || 0) })} />;
        }
        return (
          <Select
            value={record.level}
            style={{ width: 110 }}
            onChange={(value) => updateSelectedItem(record.uniqueId, { level: value })}
            options={levels.map((level) => ({ value: Number(level.level), label: `Lv.${level.level}` }))}
          />
        );
      }
    },
    {
      title: '数量',
      width: 130,
      render: (_, record) => (
        <InputNumber
          min={1}
          max={MAX_REWARD_COUNT}
          value={record.count}
          onChange={(value) => updateSelectedItem(record.uniqueId, { count: Number(value || 1) })}
        />
      )
    },
    {
      title: '操作',
      width: 80,
      render: (_, record) => <Button danger type="text" onClick={() => removeSelectedItem(record.uniqueId)}>移除</Button>
    }
  ];

  return (
    <div className="page-container">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card><Statistic title="CDK总数" value={pagination.total} prefix={<KeyOutlined />} /></Card>
        </Col>
        <Col xs={24} md={8}>
          <Card><Statistic title="启用中" value={cdks.filter((item) => Number(item.status) === 1).length} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#16a34a' }} /></Card>
        </Col>
        <Col xs={24} md={8}>
          <Card><Statistic title="本页领取次数" value={cdks.reduce((sum, item) => sum + Number(item.claim_count || 0), 0)} prefix={<GiftOutlined />} valueStyle={{ color: '#ea580c' }} /></Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} md={10}>
            <Input.Search
              allowClear
              size="large"
              placeholder="搜索CDK或名称"
              onSearch={(value) => {
                setPagination((prev) => ({ ...prev, current: 1 }));
                setKeyword(value);
              }}
            />
          </Col>
          <Col xs={24} md={4}>
            <Button icon={<ReloadOutlined />} size="large" onClick={loadCdks}>刷新</Button>
          </Col>
          <Col xs={24} md={10} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} size="large" onClick={openCreate}>创建CDK</Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={cdks}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={(nextPagination) => setPagination(nextPagination)}
        scroll={{ x: 1080 }}
      />

      <Modal
        title={editingCdk ? '编辑CDK' : '创建CDK'}
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onOk={() => form.submit()}
        width={860}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={saveCdk}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="code" label="CDK" rules={[{ required: true, message: '请输入CDK' }]}>
                <Input placeholder="例如 NEW2026" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
                <Input placeholder="新服礼包" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="备注">
            <TextArea rows={2} placeholder="后台备注，玩家不可见" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="mailTitle" label="邮件标题" rules={[{ required: true, message: '请输入邮件标题' }]}>
                <Input placeholder="CDK奖励" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="totalLimit" label="总领取上限" initialValue={0}>
                <InputNumber min={0} style={{ width: '100%' }} addonAfter="0=不限" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="mailContent" label="邮件内容" rules={[{ required: true, message: '请输入邮件内容' }]}>
            <TextArea rows={3} />
          </Form.Item>

          <Card size="small" title="奖励内容" style={{ marginBottom: 16 }}>
            <Space style={{ marginBottom: 12 }}>
              <Button type="primary" icon={<GiftOutlined />} onClick={() => setItemModalOpen(true)}>选择物品</Button>
              <Text type="secondary">同一个CDK允许多账号使用，但同账号同大区只能领取一次。</Text>
            </Space>
            <Table
              columns={selectedColumns}
              dataSource={selectedItems}
              rowKey="uniqueId"
              pagination={false}
              size="small"
              locale={{ emptyText: '还没有选择奖励物品' }}
            />
          </Card>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="startTime" label="开始时间">
                <DatePicker showTime style={{ width: '100%' }} placeholder="立即开始" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endTime" label="结束时间">
                <DatePicker showTime style={{ width: '100%' }} placeholder="永久有效" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="status" label="状态" valuePropName="checked" initialValue>
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="选择奖励物品"
        open={itemModalOpen}
        onCancel={() => setItemModalOpen(false)}
        footer={<Button type="primary" onClick={() => setItemModalOpen(false)}>完成</Button>}
        width={920}
      >
        <Input
          allowClear
          value={itemSearch}
          onChange={(event) => setItemSearch(event.target.value)}
          prefix={<SearchOutlined />}
          placeholder="搜索物品ID或名称"
          style={{ marginBottom: 12 }}
        />
        <Table
          rowKey="id"
          size="small"
          dataSource={filteredItems}
          pagination={{ pageSize: 8 }}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 110 },
            { title: '名称', dataIndex: 'name' },
            { title: '等级数', width: 90, render: (_, record) => record.levels?.length || 1 },
            {
              title: '操作',
              width: 90,
              render: (_, record) => <Button size="small" type="primary" onClick={() => addItem(record)}>加入</Button>
            }
          ]}
        />
      </Modal>

      <Drawer title="CDK详情" width={620} open={detailOpen} onClose={() => setDetailOpen(false)}>
        {currentCdk && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="CDK">{currentCdk.code}</Descriptions.Item>
            <Descriptions.Item label="名称">{currentCdk.name}</Descriptions.Item>
            <Descriptions.Item label="邮件标题">{currentCdk.mail_title}</Descriptions.Item>
            <Descriptions.Item label="邮件内容"><Paragraph>{currentCdk.mail_content}</Paragraph></Descriptions.Item>
            <Descriptions.Item label="奖励">{renderItems(currentCdk.items)}</Descriptions.Item>
            <Descriptions.Item label="领取次数">{currentCdk.claim_count || 0} / {currentCdk.total_limit > 0 ? currentCdk.total_limit : '不限'}</Descriptions.Item>
            <Descriptions.Item label="状态">{Number(currentCdk.status) === 1 ? '启用' : '停用'}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      <Modal
        title={`${currentCdk?.code || ''} 领取记录`}
        open={logsOpen}
        onCancel={() => setLogsOpen(false)}
        footer={null}
        width={1280}
      >
        <Table
          columns={logColumns}
          dataSource={logs}
          rowKey="id"
          loading={logsLoading}
          pagination={logsPagination}
          onChange={(next) => currentCdk?.id && loadLogs(currentCdk.id, next.current, next.pageSize)}
          scroll={{ x: 1280 }}
          size="small"
        />
      </Modal>
    </div>
  );
}

export default CdkManagement;
